// src/pages/student/StudentAttendance.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Alert, InputGroup
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../services/firebase";

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtAt = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—";
};
const fmtDate = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";
};
const uniqByDate = (arr, studentId, uid) => {
  const m = new Map();
  arr.forEach((d) => {
    const my = pickMyAttendance(d, studentId, uid);
    const dateKey = dayjs(toJS(d.date) || toJS(d.sessionDate) || toJS(d.createdAt)).format("YYYY-MM-DD");
    const key = `${dateKey}_${studentId || uid}`;
    if (!m.has(key)) {
      m.set(key, { ...d, ...my, __date: toJS(d.date) || toJS(d.sessionDate) || toJS(d.createdAt) });
    }
  });
  return [...m.values()];
};
const norm = (s) => String(s || "").trim().toLowerCase();

const SUBJECT_ICONS = {
  physics: "bi-lightning-charge",
  chemistry: "bi-beaker",
  biology: "bi-heart-pulse",
  zoology: "bi-bug",
  botany: "bi-flower3",
  maths: "bi-calculator",
  mathematics: "bi-calculator",
  algebra: "bi-calculator",
  geometry: "bi-rulers",
  statistics: "bi-bar-chart",
  social: "bi-people",
  "social science": "bi-people",
  history: "bi-hourglass-split",
  geography: "bi-geo-alt",
  civics: "bi-bank",
  politics: "bi-bank",
  computers: "bi-pc-display",
  "computer science": "bi-code-slash",
  coding: "bi-code-slash",
  english: "bi-book",
  hindi: "bi-translate",
  sanskrit: "bi-translate",
  tamil: "bi-translate",
  telugu: "bi-translate",
  kannada: "bi-translate",
  marathi: "bi-translate",
  french: "bi-translate",
  german: "bi-translate",
  arts: "bi-palette",
  drawing: "bi-brush",
  music: "bi-music-note-beamed",
  dance: "bi-music-note-list",
  pe: "bi-trophy",
  sports: "bi-trophy",
  economics: "bi-graph-up",
  commerce: "bi-cash-coin",
  accounts: "bi-journal-richtext",
  science: "bi-magnet",
  robotics: "bi-robot",
  default: "bi-calendar-check",
};
const subjectIcon = (subject) => SUBJECT_ICONS[norm(subject)] || SUBJECT_ICONS.default;

const showIndexLink = (err) => {
  const url = err?.message?.match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i)?.[0];
  if (url) {
    toast.info(
      <a href={url} target="_blank" rel="noreferrer">Create required Firestore index</a>,
      { autoClose: 9000 }
    );
  }
};

// ---------- audience helpers (section + branch) ----------
const same = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** Match section audience: supports `section`, `sections:[]`, or "all" */
const matchesSection = (item, studentSection) => {
  if (!studentSection) return true;
  const s = String(studentSection).trim();
  if (Array.isArray(item.sections) && item.sections.length) {
    return item.sections.some((x) => same(x, s)) || item.sections.some((x) => same(x, "all"));
  }
  if (item.section) {
    return same(item.section, s) || same(item.section, "all");
  }
  return true;
};

/** Match branch audience: supports `branch`, `branches:[]`, or "all" */
const matchesBranch = (item, studentBranch) => {
  const b = String(studentBranch || "").trim();
  if (!b) return true;
  if (Array.isArray(item.branches) && item.branches.length) {
    return item.branches.some((x) => same(x, b)) || item.branches.some((x) => same(x, "all"));
  }
  if (item.branch) {
    return same(item.branch, b) || same(item.branch, "all");
  }
  return true;
};

/** Master audience matcher */
const matchesAudience = (item, profile) =>
  matchesSection(item, profile?.section) && matchesBranch(item, profile?.branch);

/* -------- derive student's status from flexible doc shapes --------
   Supports:
   - per-student doc: { studentId, uid, status, date, subject, period, ... }
   - per-class doc with entries[]: entries: [{studentId|uid, status, markedAt, remarks}, ...]
   - per-class doc with attendeesIds[] & statuses map (best-effort)
*/
function pickMyAttendance(docData, studentId, uid) {
  const d = docData || {};
  const lower = (x) => String(x || "").toLowerCase();

  // Direct per-student doc
  if (d.studentId === studentId || d.uid === uid) {
    return {
      status: lower(d.status || ""),
      markedAt: d.markedAt || d.updatedAt || d.createdAt || null,
      remarks: d.remarks || "",
    };
  }

  // entries: array of objects
  if (Array.isArray(d.entries)) {
    const row = d.entries.find(
      (e) => e?.studentId === studentId || e?.uid === uid
    );
    if (row) {
      return {
        status: lower(row.status || ""),
        markedAt: row.markedAt || d.markedAt || d.updatedAt || d.createdAt || null,
        remarks: row.remarks || "",
      };
    }
  }

  // attendeesIds + statuses map patterns
  if (Array.isArray(d.attendeesIds)) {
    const sid = d.attendeesIds.includes(studentId)
      ? studentId
      : d.attendeesIds.includes(uid)
      ? uid
      : null;
    if (sid && d.statuses && typeof d.statuses === "object") {
      const val = d.statuses[sid];
      return {
        status: lower(val || ""),
        markedAt: d.markedAt || d.updatedAt || d.createdAt || null,
        remarks: "",
      };
    }
  }

  // Not found
  return { status: "", markedAt: null, remarks: "" };
}

/* --------------- main component --------------- */
export default function StudentAttendance() {
  const brand = useBrand();
  const { primary } = brand;

  // IDs from login
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // auth uid (rules often check this)
  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;

  // student profile
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // attendance docs (class-wide or per-student)
  const [items, setItems] = useState([]);
  const [loadingAtt, setLoadingAtt] = useState(true);

  // teacher cache
  const [people, setPeople] = useState({});

  // filters
  const [q, setQ] = useState("");
  const [fSubject, setFSubject] = useState("all");
  const [fTeacher, setFTeacher] = useState("all");
  const [fStatus, setFStatus] = useState("all"); // all | present | absent | late | excused
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [onlyAbsences, setOnlyAbsences] = useState(false);

  // particles init
  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  /* ---------- profile ---------- */
  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.");
      return;
    }
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student profile.");
        } else {
          setProfile({ id: snap.id, ...snap.data() });
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load profile.");
      })
      .finally(() => setLoadingProfile(false));
  }, [schoolId, studentId]);


  /* ---------- subscribe to attendance ---------- */
  
// 🔽 put these right above the effect
const className = profile?.className || "";
const section   = profile?.section   || "";
const branch    = profile?.branch    || "";
const isActive  = norm(profile?.status) !== "inactive";

/* ---------- subscribe to attendance ---------- */
useEffect(() => {
  if (!schoolId || !className) return;

  if (!isActive) {
    setItems([]);
    setLoadingAtt(false);
    return;
  }

  setLoadingAtt(true);
  const col = collection(db, "schools", schoolId, "attendance");

  // listeners for common shapes:
  const q1 = section
    ? query(
        col,
        where("className", "==", className),
        where("sections", "array-contains", section)
      )
    : null;

  const q2 = section
    ? query(
        col,
        where("className", "==", className),
        where("section", "in", [section, "all"])
      )
    : query(col, where("className", "==", className));

  const q3 = query(col, where("studentId", "==", studentId));

  const unsubs = [];

  const audienceOK = (it) =>
    // if you have matchesAudience(item, {section, branch}) defined:
    matchesAudience ? matchesAudience(it, { section, branch }) : true;

  const ts = (x) => {
    const d = toJS(x);
    return d ? d.getTime() : 0;
  };

  const handle = (snap) => {
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems((prev) => {
      // audience gate (section + branch) IN-MEMORY to avoid extra composite indexes
      const gated = arr.filter(audienceOK);
      const merged = uniqByDate([...prev, ...gated]);
      return merged.sort(
        (a, b) =>
          ts(b.date || b.markedAt || b.createdAt) -
          ts(a.date || a.markedAt || a.createdAt)
      );
    });
    setLoadingAtt(false);
  };

  const handleErr = (err, toastMsg = "Failed to load attendance.") => {
    console.error(err);
    showIndexLink(err);
    toast.error(toastMsg);
    setLoadingAtt(false);
  };

  if (q1) unsubs.push(onSnapshot(q1, handle, (e) => handleErr(e)));
  if (q2) unsubs.push(onSnapshot(q2, handle, (e) => handleErr(e)));
  if (q3) unsubs.push(onSnapshot(q3, handle, (e) => handleErr(e)));

  return () => unsubs.forEach((u) => u && u());
  // ✅ depend on primitives only
}, [schoolId, className, section, branch, isActive, studentId]);


  /* ---------- load teacher cache ---------- */
  useEffect(() => {
    if (!schoolId || items.length === 0) return;
    const ids = Array.from(
      new Set(
        items.flatMap((it) =>
          [it.teacherId].filter(Boolean).map((v) => String(v).trim())
        )
      )
    );
    const missing = ids.filter((id) => !(id in people));
    if (missing.length === 0) return;

    const collections = ["teachers", "staff", "users"];
    const loadOne = async (id) => {
      for (const coll of collections) {
        try {
          const refDoc = doc(db, "schools", schoolId, coll, id);
          const snap = await getDoc(refDoc);
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q1 = query(collection(db, "schools", schoolId, coll), where("uid", "==", id));
          const qs = await getDocs(q1);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q2 = query(collection(db, "schools", schoolId, coll), where("email", "==", id));
          const qs = await getDocs(q2);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      return null;
    };

    (async () => {
      const entries = await Promise.all(missing.map(async (id) => [id, await loadOne(id)]));
      const next = Object.fromEntries(entries);
      if (Object.keys(next).length) setPeople((p) => ({ ...p, ...next }));
    })();
  }, [schoolId, items, people]);

  /* ---------- audience-scoped items ---------- */
  const audienceItems = useMemo(
    () => items.filter((it) => matchesAudience(it, profile)),
    [items, profile]
  );

  /* ---------- derive "my" row + normalized items ---------- */
const normalized = useMemo(() => {
  return uniqByDate(audienceItems, studentId, uid).map((d) => ({
    ...d,
    __myStatus: d.status || "",
    __myMarkedAt: d.markedAt || null,
    __myRemarks: d.remarks || "",
    __subject: d.subject || d.title || "Attendance",
  }));
}, [audienceItems, studentId, uid]);

  /* ---------- filter options ---------- */
  const subjectOptions = useMemo(() => {
    const s = new Set();
    normalized.forEach((it) => {
      const sub = (it.__subject || "").trim();
      if (sub) s.add(sub);
    });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [normalized]);

  const teacherOptions = useMemo(() => {
    const names = new Set();
    normalized.forEach((a) => {
      const tKey = a.teacherId ? String(a.teacherId).trim() : "";
      const teacher = tKey ? people[tKey] : null;
      const teacherName =
        teacher?.displayName ||
        teacher?.name ||
        [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
        teacher?.fullName ||
        a.teacherName ||
        (typeof a.teacher === "string" ? a.teacher : "") ||
        "";
      if (teacherName) names.add(teacherName);
    });
    return ["all", ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [normalized, people]);

  /* ---------- filtered list ---------- */
  const filtered = useMemo(() => {
    const base = normalized.slice();
    return base
      .filter((a) => {
        const st = norm(a.__myStatus);
        if (onlyAbsences && st !== "absent") return false;

        if (fStatus !== "all" && st !== norm(fStatus)) return false;

        if (fSubject !== "all") {
          if ((a.__subject || "") !== fSubject) return false;
        }

        if (fTeacher !== "all") {
          const tKey = a.teacherId ? String(a.teacherId).trim() : "";
          const teacher = tKey ? people[tKey] : null;
          const teacherName =
            teacher?.displayName ||
            teacher?.name ||
            [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
            teacher?.fullName ||
            a.teacherName ||
            (typeof a.teacher === "string" ? a.teacher : "") ||
            "";
          if (teacherName !== fTeacher) return false;
        }

        // date range
        const d = a.__date;
        if (fromDate && d && dayjs(d).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && d && dayjs(d).isAfter(dayjs(toDate).endOf("day"))) return false;

        // text query
        if (q.trim()) {
          const hay = [
            a.__subject, a.teacherName, a.className, a.section, a.__myRemarks,
            ...(Array.isArray(a.sections) ? a.sections : []),
            a.branch, ...(Array.isArray(a.branches) ? a.branches : []),
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          if (!hay.includes(q.trim().toLowerCase())) return false;
        }

        return true;
      })
      .sort((a, b) => (toJS(b.__date) - toJS(a.__date)));
  }, [normalized, onlyAbsences, fStatus, fSubject, fTeacher, fromDate, toDate, q, people]);

  /* ---------- summary ---------- */
  const summary = useMemo(() => {
    let present = 0, absent = 0, late = 0, excused = 0, total = 0;
    normalized.forEach((a) => {
      const st = norm(a.__myStatus);
      if (!st) return; // skip unknown
      total += 1;
      if (st === "present") present++;
      else if (st === "absent") absent++;
      else if (st === "late") late++;
      else if (st === "excused") excused++;
    });
    const pct = total ? Math.round((present / total) * 100) : 0;
    return { present, absent, late, excused, total, pct };
  }, [normalized]);

  /* ---------- UI helpers ---------- */
  const StatusBadge = ({ status }) => {
    const s = norm(status);
    if (s === "present") return <Badge bg="success">Present</Badge>;
    if (s === "absent") return <Badge bg="danger">Absent</Badge>;
    if (s === "late") return <Badge bg="warning" text="dark">Late</Badge>;
    if (s === "excused") return <Badge bg="info" text="dark">Excused</Badge>;
    return <Badge bg="secondary">—</Badge>;
  };

  const statusChipClass = (s) => {
    const v = norm(s);
    if (v === "present") return "present";
    if (v === "absent") return "absent";
    if (v === "late") return "late";
    if (v === "excused") return "excused";
    return "unknown";
  };

  return (
    <div className="position-relative overflow-hidden">
      {/* FULL-PAGE background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primary, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(brand.accent,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="student-attendance-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 60, density: { enable: true, area: 900 } },
            color: { value: [brand.primary, brand.accent] },
            links: { enable: true, color: hexToRgba(brand.primary, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.0, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 36 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 20 } } } },
          ],
        }}
      />

      {/* Foreground */}
      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer
          position="bottom-right"
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          theme="dark"
        />

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Attendance</h5>
            <small className="text-muted">
              {studentName || "Student"} • Class {[profile?.className, profile?.section].filter(Boolean).join("-") || "—"}
              {profile?.branch ? ` • Branch ${profile.branch}` : ""}
              {isActive ? "" : " • Inactive"}
            </small>
          </div>

          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="absent-switch"
              label="Only absences"
              checked={onlyAbsences}
              onChange={(e) => setOnlyAbsences(e.target.checked)}
              disabled={!isActive}
            />
          </div>
        </div>

        {/* Summary */}
        {isActive && (
          <Row className="g-3 mb-3">
            <Col xs={12} lg={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Attendance %</div>
                    <div className="fs-3 fw-semibold">{summary.pct}%</div>
                    <div className="text-muted small">{summary.present} / {summary.total} present</div>
                  </div>
                  <div className="subj-ico d-inline-flex align-items-center justify-content-center" title="Attendance">
                    <i className="bi bi-clipboard-check" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={8}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex flex-wrap gap-2">
                  <Badge bg="success" className="px-3 py-2">Present: {summary.present}</Badge>
                  <Badge bg="danger" className="px-3 py-2">Absent: {summary.absent}</Badge>
                  <Badge bg="warning" text="dark" className="px-3 py-2">Late: {summary.late}</Badge>
                  <Badge bg="info" text="dark" className="px-3 py-2">Excused: {summary.excused}</Badge>
                  <Badge bg="secondary" className="px-3 py-2">Total: {summary.total}</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        {isActive && (
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="py-3">
              <Row className="g-2">
                <Col xs={12} md={4} lg={3}>
                  <InputGroup>
                    <InputGroup.Text className="bg-light"><i className="bi bi-search" /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search subject / teacher / remarks"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                    {subjectOptions.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fTeacher} onChange={(e) => setFTeacher(e.target.value)}>
                    {teacherOptions.map((t) => (
                      <option key={t} value={t}>{t === "all" ? "All teachers" : t}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="excused">Excused</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={1}>
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    title="From date"
                  />
                </Col>
                <Col xs={6} md={4} lg={1}>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    title="To date"
                  />
                </Col>

                <Col xs={12} md={3} lg={2} className="d-grid">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setQ(""); setFSubject("all"); setFTeacher("all");
                      setFStatus("all"); setFromDate(""); setToDate(""); setOnlyAbsences(false);
                    }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Inactive notice */}
        {!loadingProfile && !isActive ? (
          <Alert variant="warning" className="mb-3">
            Your student account is currently <b>inactive</b>. Please contact your school
            management to activate your account to access attendance.
          </Alert>
        ) : null}

        {/* List / Loading */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            {loadingProfile ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : !isActive ? (
              <div className="text-muted py-4 text-center">
                Attendance is hidden until your account is activated.
              </div>
            ) : loadingAtt ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading attendance…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-muted py-4 text-center">No attendance records found.</div>
            ) : (
              <Row className="g-3">
                {filtered.map((a) => {
                  const subj = a.__subject || "Attendance";
                  const ico = subjectIcon(subj);

                  const tKey = a.teacherId ? String(a.teacherId).trim() : "";
                  const teacher = tKey ? people[tKey] : null;
                  const teacherName =
                    teacher?.displayName ||
                    teacher?.name ||
                    [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
                    teacher?.fullName ||
                    a.teacherName ||
                    (typeof a.teacher === "string" ? a.teacher : "") ||
                    "—";

                  const date = a.__date;
                  const status = a.__myStatus || "";

                  return (
                    <Col key={a.id} xs={12} md={6} lg={4}>
                      <Card
                        className={`h-100 shadow-sm border-0 attendance-card ${statusChipClass(status)}`}
                        style={{ borderColor: hexToRgba(primary, 0.22), position: "relative", overflow: "hidden" }}
                      >
                        <div className="card-particles" aria-hidden />

                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={subj}>
                                <i className={`bi ${ico}`} />
                              </div>
                              <div className="fw-semibold text-truncate" title={subj}>
                                {subj}
                                <div className="small text-muted">{fmtDate(date)}</div>
                              </div>
                            </div>
                            <StatusBadge status={status} />
                          </div>

                          <div className="small d-flex align-items-center gap-2">
                            <i className="bi bi-person-badge" />
                            <span className="text-muted">Teacher:</span>
                            <span className="fw-semibold text-truncate">{teacherName}</span>
                          </div>

                          {a.period ? (
                            <div className="text-muted small mt-1">Period: <span className="fw-semibold">{a.period}</span></div>
                          ) : null}

                          {a.__myRemarks ? (
                            <div className="small mt-2 text-truncate" title={a.__myRemarks}>
                              Remarks: {a.__myRemarks}
                            </div>
                          ) : null}

                          {a.__myMarkedAt ? (
                            <div className="text-muted small mt-2">
                              Marked at: <span className="fw-semibold">{fmtAt(a.__myMarkedAt)}</span>
                            </div>
                          ) : null}

                          <div className="mt-auto pt-3 d-flex justify-content-between align-items-center">
                            <Badge bg="light" text="dark" className="border">
                              {a.className || profile?.className || "—"}
                              {a.section
                                ? `-${a.section}`
                                : Array.isArray(a.sections) && a.sections.length
                                ? `-${a.sections.join(",")}`
                                : profile?.section ? `-${profile.section}` : ""}
                              {a.branch ? ` • ${a.branch}` :
                                Array.isArray(a.branches) && a.branches.length ? ` • ${a.branches.join(",")}` :
                                profile?.branch ? ` • ${profile.branch}` : ""}
                            </Badge>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Styles */}
      <style>{`
        /* Toastify themed to accent/primary */
        .Toastify__toast {
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid ${hexToRgba(brand.accent, 0.4)};
          background: linear-gradient(
            135deg,
            ${hexToRgba(brand.primary, 0.14)} 0%,
            ${hexToRgba(brand.accent, 0.14)} 100%
          );
          backdrop-filter: blur(6px);
        }
        .Toastify__toast-body { color: ${brand.accent}; font-weight: 500; }
        .Toastify__progress-bar { background: ${brand.accent}; }

        .attendance-card {
          border-radius: 18px;
          background: linear-gradient(
            180deg,
            ${hexToRgba(brand.primary, 0.06)} 0%,
            transparent 60%
          );
          transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease, filter .18s ease;
        }
        .attendance-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1.2rem rgba(0,0,0,.12); }

        /* State tints */
        .attendance-card.present { box-shadow: 0 .35rem .9rem ${hexToRgba("#28a745", .22)}; }
        .attendance-card.absent { box-shadow: 0 .35rem .9rem ${hexToRgba("#dc3545", .22)}; }
        .attendance-card.late { box-shadow: 0 .35rem .9rem ${hexToRgba("#ffc107", .22)}; }
        .attendance-card.excused { box-shadow: 0 .35rem .9rem ${hexToRgba("#0dcaf0", .22)}; }

        /* Bigger subject icon */
        .subj-ico {
          width: 64px; height: 64px; border-radius: 16px;
          background: ${hexToRgba(brand.primary, 0.18)};
          color: ${brand.primary};
          border: 1px solid ${hexToRgba(brand.primary, 0.30)};
          font-size: 34px;
        }
        @media (max-width: 576px) {
          .subj-ico { width: 54px; height: 54px; font-size: 28px; }
        }

        /* Per-card animated particle overlay (pure CSS) */
        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(${hexToRgba(brand.primary, .18)} 1px, transparent 1px),
            radial-gradient(${hexToRgba(brand.accent, .18)} 1px, transparent 1px);
          background-position: 0 0, 12px 12px;
          background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite;
          opacity: .22;
        }
        .attendance-card.present .card-particles { animation-duration: 8s; }
        .attendance-card.absent .card-particles { animation-duration: 6s; opacity: .28; }
        .attendance-card.late .card-particles { animation-duration: 7.5s; opacity: .26; }
        .attendance-card.excused .card-particles { animation-duration: 9.5s; opacity: .24; }

        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }
      `}</style>
    </div>
  );
}
